import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ButtonComponent } from '../../buttons/button/button.component';
import { AppButton } from '../../../../../shared/models/button.model';
import { InteractionService } from '../../../../../../service/interaction/interaction.service';
import { Subject, takeUntil } from 'rxjs';

export interface FeedbackOption {
  feedback_metadata_id: number;
  feedback_value: string;
  feedback_type: string;
}

export interface LikeDislikeDialogData {
  dialogType: 'like' | 'dislike';
  existingData?: {
    selectedOption: number | null;
    comment: string;
  };
  isEditable?: boolean;
}

export interface LikeDislikeDialogResult {
  selectedOption: number | null;
  comment: string;
}

/**
 * LikeDislikeDialogComponent (v2)
 *
 * A standalone dialog component for collecting user feedback (like/dislike)
 * with optional radio options and a comments textarea.
 */
@Component({
  selector: 'app-like-dislike-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ButtonComponent
  ],
  templateUrl: './like-dislike-dialog.component.html',
  styleUrl: './like-dislike-dialog.component.scss'
})
export class LikeDislikeDialogComponent implements OnInit, OnDestroy {
  radioOptions: FeedbackOption[] = [];
  selectedOption: number | null = null;
  comment = '';
  showError = false;
  commentError = false;
  isLikeDialog: boolean;
  isSubmitting = false;

  private readonly destroy$ = new Subject<void>();

  readonly cancelButton: AppButton = {
    extraClass: 'btn-white-bdr',
    label: 'Cancel',
    type: 'normal'
  };

  readonly submitButton: AppButton = {
    label: 'Submit',
    type: 'normal'
  };

  constructor(
    public dialogRef: MatDialogRef<LikeDislikeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LikeDislikeDialogData,
    private readonly apiService: InteractionService
  ) {
    this.isLikeDialog = data.dialogType === 'like';
    if (data.existingData) {
      this.selectedOption = data.existingData.selectedOption;
      this.comment = data.existingData.comment;
    }
  }

  ngOnInit(): void {
    this.loadRadioOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads radio button options from the API service based on dialog type.
   * Filters options by 'Positive' or 'Negative' feedback type.
   */
  loadRadioOptions(): void {
    const feedbackType = this.isLikeDialog ? 'Positive' : 'Negative';

    this.apiService.data$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.radioOptions = this.filterByFeedbackType(data, feedbackType);
      },
      error: (error) => {
        console.error(`Error fetching ${feedbackType.toLowerCase()} feedback options:`, error);
      }
    });
  }

  /**
   * Filters feedback options by feedback type (Positive/Negative).
   */
  private filterByFeedbackType(options: FeedbackOption[], type: string): FeedbackOption[] {
    return options.filter(option => option.feedback_type === type);
  }

  /** Clears the selection validation error when a radio option is selected. */
  onOptionChange(): void {
    this.showError = false;
  }

  /** Validates comment field on change. */
  onCommentChange(value: string): void {
    this.commentError = !(value?.trim().length > 0);
  }

  /** Validates and submits the feedback form. */
  onSubmit(): void {
    if (!this.selectedOption) {
      this.showError = true;
      return;
    }

    if (!this.isLikeDialog && !this.comment?.trim()) {
      this.commentError = true;
      return;
    }

    this.dialogRef.close({
      selectedOption: this.selectedOption,
      comment: this.comment,
    } as LikeDislikeDialogResult);
  }

  /** Closes the dialog without submitting. */
  onCancel(): void {
    this.dialogRef.close();
  }
}
